// SegBar — horizontal stacked segmented bar (allocation / mix visualizations).
// Each segment is flex-weighted by its value and may print a `caption` inside the band
// (the share, on a bar tall enough to hold text); a segment given `tooltip` content
// shows it on hover AND on keyboard focus — the segment becomes focusable for
// exactly that reason, since a bare <span> can't be tabbed to and the tooltip
// would otherwise be mouse-only. Segments with nothing to say stay inert.
// Custom CSS (no chart lib) to match the prototype. Used on Dashboard,
// Portfolio and Manage.
import PropTypes from 'prop-types';
import { Tooltip } from '../data-display/Tooltip';
import './seg-bar.css';

export function SegBar({
  segments, interactive = false, height = 10, rounded = true, className,
  tooltipVariant = 'pill',
}) {
  return (
    <div
      className={['seg-bar', interactive && 'seg-bar--interactive', rounded && 'seg-bar--rounded', className]
        .filter(Boolean)
        .join(' ')}
      style={{ height }}
      role="img"
      aria-label={segments.map((s) => `${s.label || ''} ${s.value}`).join(', ')}
    >
      {segments.map((s, i) => (
        <Tooltip
          key={i}
          content={s.tooltip}
          side="bottom"
          variant={tooltipVariant}
          className="seg-bar__seg"
          style={{ flex: s.value, background: s.color }}
          tabIndex={s.tooltip ? 0 : undefined}
        >
          {/* Optional in-band caption (the sponsor population bar prints each share
              inside its own segment). Callers that pass nothing render as before. */}
          {s.caption && <span className="seg-bar__caption">{s.caption}</span>}
        </Tooltip>
      ))}
    </div>
  );
}

SegBar.propTypes = {
  /** [{ value, color, tooltip?, label?, caption? }] — `caption` prints inside the band. */
  segments: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number.isRequired,
      color: PropTypes.string.isRequired,
      tooltip: PropTypes.node,
      label: PropTypes.string,
      caption: PropTypes.node,
    }),
  ).isRequired,
  interactive: PropTypes.bool,
  /** 'card' for structured tooltip bodies — see Tooltip. */
  tooltipVariant: PropTypes.oneOf(['pill', 'card']),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  rounded: PropTypes.bool,
  className: PropTypes.string,
};

export default SegBar;
